import axios from 'axios';
import { apiClient } from '../../apiHelper';

// const DUMMY_PIXEL_SPACING_VALUE = '0.2,0.2';
// const DUMMY_PIXEL_SPACING_UNIT = 'mm';
const _TESTING_IS_FROM_MOBILE = true;


export interface VideoFilePayload {
  file_name: string;
  width: number | null;
  height: number | null;
  duration: number | null;
  pixel_spacing_value?: string | null;
  pixel_spacing_unit?: string | null;
  sweep_index?: number;
}

export interface UnifiedStudyGroup {
  examType: 'OB' | 'BLIND';
  studyCategory: 'T1' | 'T2' | 'T3';
  study_date?: string | null;
  sop_instance_uids?: string[];
  dicomFiles?: File[];
  videoFiles?: {
    file: File;
    width?: number;
    height?: number;
    duration?: number;
    sweep_index?: number;
  }[];
}

export interface UnifiedUploadParams {
  study_instance_uid?: string;
  existing_patient_id?: string;
  blind_prediction?: boolean;
  patient_data?: {
    patient_name: string;
    patient_last_name: string;
    patient_id?: string;
  };
  pregnancy_data?: {
    dating_method: string;
    ultrasound_date: string;
    last_menstrual_period?: string | null;
    estimated_due_date?: string | null;
    preg_crl?: number | null;
    preg_bpd?: number | null;
    preg_hc?: number | null;
    preg_ac?: number | null;
    preg_fl?: number | null;
  };
  studies: UnifiedStudyGroup[];
}

export interface UnifiedUploadResponse {
  id: string;
  status: string;
  patient_id: string;
}

interface UploadWorkflowErrorShape {
  message?: string;
  detail?: string | Array<{ msg?: string }>;
  status_code?: number;
  error?: string;
  details?: Record<string, unknown>;
  response?: {
    status?: number;
    data?: {
      message?: string;
      detail?: string | Array<{ msg?: string }>;
      status_code?: number;
      error?: string;
      details?: Record<string, unknown>;
    };
  };
}

class UnifiedUploadWorkflowError extends Error {
  statusCode?: number;
  details?: Record<string, unknown>;
  failedKeys: string[];

  constructor(
    message: string,
    options?: {
      statusCode?: number;
      details?: Record<string, unknown>;
      failedKeys?: string[];
    }
  ) {
    super(message);
    this.name = 'UnifiedUploadWorkflowError';
    this.statusCode = options?.statusCode;
    this.details = options?.details;
    this.failedKeys = options?.failedKeys ?? [];
  }
}

function extractUploadError(error: unknown): {
  message: string;
  statusCode?: number;
  details?: Record<string, unknown>;
} {
  if (!error || typeof error !== 'object') {
    return {
      message: error instanceof Error ? error.message : String(error),
    };
  }

  const candidate = error as UploadWorkflowErrorShape;
  const responseData = candidate.response?.data;
  const detail = responseData?.detail ?? candidate.detail;
  let message =
    responseData?.message ??
    candidate.message ??
    (typeof detail === 'string' ? detail : undefined) ??
    (error instanceof Error ? error.message : 'Upload failed');

  if (Array.isArray(detail) && detail.length > 0) {
    message = detail[0]?.msg || message;
  }

  return {
    message,
    statusCode: responseData?.status_code ?? candidate.status_code ?? candidate.response?.status,
    details: responseData?.details ?? candidate.details,
  };
}

/** One file to upload in sweep flow (dicom or video) */
export type SweepUploadItem =
  | {
      kind: 'dicom';
      examType: 'OB' | 'BLIND';
      studyCategory: 'T1' | 'T2' | 'T3';
      fileKey: string;
      file: File;
    }
  | {
      kind: 'video';
      examType: 'OB' | 'BLIND';
      studyCategory: 'T1' | 'T2' | 'T3';
      fileKey: string;
      file: File;
      videoMeta: {
        file_name: string;
        width?: number;
        height?: number;
        duration?: number;
        pixel_spacing_value?: string;
        pixel_spacing_unit?: string;
        sweep_index?: number;
      };
    };

export interface SweepResponse {
  upload_session_id: string;
  study_instance_uid: string;
  status: string;
  missing_keys: string[];
  serie_instance_uid?: string;
  sop_instance_uid?: string;
  prediction_report?: unknown;
}

export interface UploadSessionByStudy {
  upload_session_id: string;
  study_instance_uid: string;
  status?: string;
  missing_keys: string[];
  study_category?: string;
  serie_instance_uid?: string;
}

/** One incomplete BLIND series in a study (multiple BS series per study supported). */
export interface UploadSessionItem {
  serie_instance_uid: string;
  upload_session_id: string;
  missing_keys: string[];
  study_category?: string;
}

/** Response when study has one or more incomplete BLIND series. */
export interface UploadSessionsByStudy {
  study_instance_uid: string;
  sessions: UploadSessionItem[];
}

/** Get incomplete upload sessions for a study (one per incomplete BLIND series). Returns null if no incomplete series. */
export async function getUploadSessionByStudy(
  studyInstanceUid: string
): Promise<UploadSessionsByStudy | null> {
  try {
    const response = await apiClient.get(
      `/upload/workflow/unified-case/session?study_instance_uid=${encodeURIComponent(studyInstanceUid)}`
    );
    // API may return { data: { study_instance_uid, sessions } } or axios interceptor may unwrap so response is that
    const body = response && typeof response === 'object' ? response : null;
    const data =
      (body && (body as { data?: UploadSessionsByStudy }).data) ??
      (body && Array.isArray((body as UploadSessionsByStudy).sessions)
        ? (body as UploadSessionsByStudy)
        : null);
    const sessions = data?.sessions;
    if (!Array.isArray(sessions) || sessions.length === 0) return null;
    return {
      study_instance_uid: data.study_instance_uid ?? studyInstanceUid,
      sessions,
    };
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw e;
  }
}

/** Build flat list of items (one per file) for sweep upload from UnifiedUploadParams */
export function buildUploadItems(
  params: UnifiedUploadParams
): SweepUploadItem[] {
  const items: SweepUploadItem[] = [];
  for (const group of params.studies) {
    const uids = group.sop_instance_uids || [];
    const dicomFiles = group.dicomFiles || [];
    if (dicomFiles.length) {
      dicomFiles.forEach((file: File, i: number) => {
        const uid = uids[i] ?? file.name;
        items.push({
          kind: 'dicom',
          examType: group.examType,
          studyCategory: group.studyCategory,
          fileKey: uid,
          file,
        });
      });
    }
    const videoFiles = group.videoFiles || [];
    videoFiles.forEach((item, vIdx) => {
      const file = item.file;
      const fileName = file.name;
      const resolvedSweepIndex =
        group.examType === 'BLIND'
          ? (item.sweep_index ?? vIdx + 1)
          : item.sweep_index;
      items.push({
        kind: 'video',
        examType: group.examType,
        studyCategory: group.studyCategory,
        fileKey: fileName,
        file,
        videoMeta: {
          file_name: fileName,
          width: item.width ?? undefined,
          height: item.height ?? undefined,
          duration: item.duration ?? undefined,
          // pixel_spacing_value: DUMMY_PIXEL_SPACING_VALUE,
          // pixel_spacing_unit: DUMMY_PIXEL_SPACING_UNIT,
          ...(resolvedSweepIndex !== undefined
            ? { sweep_index: resolvedSweepIndex }
            : {}),
        },
      });
    });
  }
  return items;
}

/** Payload for one sweep request (first or continuation) */
interface SweepPayloadFirst {
  file_key: string;
  examType: 'OB' | 'BLIND';
  studyCategory: 'T1' | 'T2' | 'T3';
  study_instance_uid?: string;
  blind_prediction?: boolean;
  video_meta?: {
    file_name: string;
    width?: number;
    height?: number;
    duration?: number;
    total_frames?: number;
    pixel_spacing_value?: string;
    pixel_spacing_unit?: string;
    sweep_index?: number;
  };
  existing_patient_id?: string;
  patient_data?: UnifiedUploadParams['patient_data'];
  pregnancy_data?: UnifiedUploadParams['pregnancy_data'];
  studies: Array<{
    examType: 'OB' | 'BLIND';
    studyCategory: 'T1' | 'T2' | 'T3';
    sop_instance_uids: string[];
    assigned_video_files: Array<{
      file_name: string;
      width?: number;
      height?: number;
      duration?: number;
      pixel_spacing_value?: string;
      pixel_spacing_unit?: string;
      sweep_index?: number;
    }>;
  }>;

}

interface SweepPayloadContinuation {
  upload_session_id: string;
  file_key: string;
  examType: 'OB' | 'BLIND';
  studyCategory: 'T1' | 'T2' | 'T3';
  blind_prediction?: boolean;
  video_meta?: {
    file_name: string;
    width?: number;
    height?: number;
    duration?: number;
    pixel_spacing_value?: string;
    pixel_spacing_unit?: string;
    sweep_index?: number;
  };
}


export const uploadUnifiedSweep = async (
  payload: SweepPayloadFirst | SweepPayloadContinuation,
  file: File,
  fileKey: string
): Promise<SweepResponse> => {
  const formData = new FormData();
  const finalPayload = _TESTING_IS_FROM_MOBILE
    ? { ...payload, convert_to_dicom: true }
    : payload;
  formData.append('payload', JSON.stringify(finalPayload));
  formData.append(fileKey, file);

  const response = await apiClient.post(
    '/upload/workflow/unified-case/file',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  const body = response.data as { data?: SweepResponse } & SweepResponse;
  return body?.data ?? body;
};

/** Upload all files one-by-one (sweep flow). Returns last response data and list of failed keys for UI to keep failed and remove succeeded. */
export const uploadUnifiedCaseSweeps = async (
  params: UnifiedUploadParams
): Promise<{
  data: SweepResponse;
  failed: { key: string; reason: string }[];
  successKeys: string[];
}> => {
  const items = buildUploadItems(params);
  if (items.length === 0) {
    throw new Error('No files to upload');
  }
  const failed: { key: string; reason: string }[] = [];
  const successKeys: string[] = [];
  let lastData: SweepResponse | null = null;
  let uploadSessionId: string | null = null;

  const manifestStudies = params.studies.map((g) => ({
    examType: g.examType,
    studyCategory: g.studyCategory,
    sop_instance_uids: g.sop_instance_uids || [],
    assigned_video_files: (g.videoFiles || []).map((v, vIdx) => {
      const resolvedSweepIndex =
        g.examType === 'BLIND' ? (v.sweep_index ?? vIdx + 1) : v.sweep_index;
      return {
        file_name: v.file.name,
        width: v.width ?? null,
        height: v.height ?? null,
        duration: v.duration ?? null,
        // pixel_spacing_value: DUMMY_PIXEL_SPACING_VALUE,
        // pixel_spacing_unit: DUMMY_PIXEL_SPACING_UNIT,
        ...(resolvedSweepIndex !== undefined
          ? { sweep_index: resolvedSweepIndex }
          : {}),
      };
    }),
  }));

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const fileKey = item.fileKey;
    try {
      // First sweep: send patient (existing or new) + pregnancy + manifest. Others: only session + file (patient already assigned to exam).
      const payload: SweepPayloadFirst | SweepPayloadContinuation =
        uploadSessionId == null
          ? {
              file_key: fileKey,
              examType: item.examType,
              studyCategory: item.studyCategory,
              ...(item.examType === 'BLIND' || params.blind_prediction === true
                ? { blind_prediction: true }
                : {}),
              ...(item.kind === 'video' ? { video_meta: item.videoMeta } : {}),
              ...(params.study_instance_uid
                ? { study_instance_uid: params.study_instance_uid }
                : {}),
              ...(!params.study_instance_uid && params.existing_patient_id
                ? { existing_patient_id: params.existing_patient_id }
                : {}),
              ...(!params.study_instance_uid && params.patient_data
                ? { patient_data: params.patient_data }
                : {}),
              ...(!params.study_instance_uid && params.pregnancy_data
                ? { pregnancy_data: params.pregnancy_data }
                : {}),
              studies: manifestStudies,
            }
          : {
              upload_session_id: uploadSessionId,
              file_key: fileKey,
              examType: item.examType,
              studyCategory: item.studyCategory,
              ...(item.examType === 'BLIND' || params.blind_prediction === true
                ? { blind_prediction: true }
                : {}),
              ...(item.kind === 'video' ? { video_meta: item.videoMeta } : {}),
            };
      const res = await uploadUnifiedSweep(payload, item.file, fileKey);
      lastData = res;
      uploadSessionId = res.upload_session_id;
      successKeys.push(fileKey);
    } catch (err: unknown) {
      const uploadError = extractUploadError(err);
      failed.push({ key: fileKey, reason: uploadError.message });

      const isLastFileFailure = successKeys.length === 0 && failed.length === items.length;
      if (isLastFileFailure) {
        throw new UnifiedUploadWorkflowError(uploadError.message, {
          statusCode: uploadError.statusCode,
          details: uploadError.details,
          failedKeys: failed.map((item) => item.key),
        });
      }
    }
  }

  if (!lastData) {
    const firstFailure = failed[0];
    throw new UnifiedUploadWorkflowError(
      firstFailure?.reason || 'Upload failed',
      {
        failedKeys: failed.map((item) => item.key),
      }
    );
  }

  return {
    data: lastData,
    failed,
    successKeys,
  };
};

export const uploadUnifiedCase = async (
  params: UnifiedUploadParams
): Promise<UnifiedUploadResponse> => {
  try {
    const formData = new FormData();
    const payloadStudies = [];

    for (const group of params.studies) {
      const sopInstanceUids: string[] = group.sop_instance_uids || [];
      const videoPayloads: VideoFilePayload[] = [];

      if (group.dicomFiles) {
        group.dicomFiles.forEach((file: File, index: number) => {
          const uid = sopInstanceUids[index];

          if (!uid) {
            console.error(
              'Critical: Mismatch between file count and UID count'
            );
            const fallbackUid = file.name;
            sopInstanceUids[index] = fallbackUid;
            formData.append(fallbackUid, file);
          } else {
            formData.append(uid, file);
          }
        });
      }

      if (group.videoFiles) {
        group.videoFiles.forEach((item, vIdx) => {
          const fileObj = item.file;
          const fileName = fileObj.name;
          const resolvedSweepIndex =
            group.examType === 'BLIND'
              ? (item.sweep_index ?? vIdx + 1)
              : item.sweep_index;

          videoPayloads.push({
            file_name: fileName,
            width: item.width || 0,
            height: item.height || 0,
            duration: item.duration || 0,
            // pixel_spacing_value: DUMMY_PIXEL_SPACING_VALUE,
            // pixel_spacing_unit: DUMMY_PIXEL_SPACING_UNIT,
            ...(resolvedSweepIndex !== undefined
              ? { sweep_index: resolvedSweepIndex }
              : {}),
          });
          formData.append(fileName, fileObj);
        });
      }

      payloadStudies.push({
        examType: group.examType,
        studyCategory: group.studyCategory,
        study_date: group.study_date || null,
        sop_instance_uids: sopInstanceUids,
        assigned_video_files: videoPayloads,
      });
    }

    const jsonPayload: any = {
      studies: payloadStudies,
      ...(params.existing_patient_id
        ? { existing_patient_id: params.existing_patient_id }
        : {}),
      ...(params.patient_data ? { patient_data: params.patient_data } : {}),
      ...(params.pregnancy_data
        ? { pregnancy_data: params.pregnancy_data }
        : {}),
    };

    console.log('[UnifiedUpload] Sending Payload:', jsonPayload);
    formData.append('payload', JSON.stringify(jsonPayload));

    const response = await apiClient.post(
      '/upload/workflow/unified-case',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('[uploadUnifiedCase] Error:', error);
    throw error;
  }
};
